import {
  formatDisplayDate,
  normalizeComparableSex,
} from "./recordDetailsUtils";



/** Table cells: null, undefined, or whitespace-only strings show as Unspecified. */
export const formatTableCell = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string" && value.trim() === "") {
    return "";
  }
  return value;
};

const parseCoordinateValue = (value, axis) => {
  if (value === null || value === undefined) {
    return null;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  const upperRaw = raw.toUpperCase();
  if (upperRaw === "NULL" || upperRaw === "N/A" || upperRaw === "-") {
    return null;
  }

  const hemisphereMatch = upperRaw.match(/[NSEW]/);
  const hemisphere = hemisphereMatch ? hemisphereMatch[0] : null;
  const isNegativeHemisphere = hemisphere === "S" || hemisphere === "W";

  const normalized = upperRaw
    .replace(/[º°]/g, " ")
    .replace(/[′']/g, " ")
    .replace(/[″"]/g, " ")
    .replace(/,/g, " ");

  const numericParts = normalized.match(/-?\d+(?:\.\d+)?/g);
  if (!numericParts || numericParts.length === 0) {
    return null;
  }

  const numbers = numericParts.map((part) => Number.parseFloat(part));
  if (numbers.some((num) => Number.isNaN(num))) {
    return null;
  }

  const degrees = Math.abs(numbers[0]);
  const minutes = numbers[1] ? Math.abs(numbers[1]) : 0;
  const seconds = numbers[2] ? Math.abs(numbers[2]) : 0;
  let decimal = degrees + minutes / 60 + seconds / 3600;

  if (hemisphere) {
    if (axis === "lat" && !["N", "S"].includes(hemisphere)) {
      return null;
    }
    if (axis === "lon" && !["E", "W"].includes(hemisphere)) {
      return null;
    }
    if (isNegativeHemisphere) {
      decimal *= -1;
    }
  } else if (numbers[0] < 0) {
    decimal *= -1;
  }

  const limit = axis === "lat" ? 90 : 180;
  if (Math.abs(decimal) > limit) {
    return null;
  }

  return decimal;
};

const parseDecimalCoordinates = (latitude, longitude) => {
  const lat = parseCoordinateValue(latitude, "lat");
  const lon = parseCoordinateValue(longitude, "lon");

  if (lat === null || lon === null) {
    return null;
  }

  return { lat, lon };
};

const formatDecimalCoordinate = (value) => Number(value).toFixed(6);

const normalizeAvifaunaRecords = (data) => {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (typeof data === "object") {
    return [data];
  }
  return [];
};

export const parseAvifaunaRingLookup = (resightingDetails) => {
  if (!resightingDetails) {
    return { ringNumber: "", ringSize: "" };
  }

  const ringNo = resightingDetails.ring_no;
  const ringSize = resightingDetails.ring_size;
  if (ringNo != null && ringNo !== "" && ringSize != null && ringSize !== "") {
    return {
      ringNumber: String(ringNo).trim(),
      ringSize: String(ringSize).trim(),
    };
  }

  const combined = resightingDetails.ring_number;
  if (!combined) {
    return { ringNumber: "", ringSize: "" };
  }

  const str = String(combined).trim();
  const dashIndex = str.indexOf("-");
  if (dashIndex <= 0) {
    return { ringNumber: str, ringSize: "" };
  }

  return {
    ringSize: str.slice(0, dashIndex).trim(),
    ringNumber: str.slice(dashIndex + 1).trim(),
  };
};

export const buildAvifaunaRecordDetailsFromApi = (data, options = {}) => {
  const { detailDateFormatOptions = {} } = options;
  const records = normalizeAvifaunaRecords(data);
  const ringing = records.find((row) => row?.record_type === "ringing") || null;
  const resightings = records.filter((row) => row?.record_type === "resighting");

  const ringingRows = ringing
    ? [
      ["Ring number", formatTableCell(`${ringing.ring_size}-${ringing.ring_number}`)],
      // ["Ring size", formatTableCell(ringing.ring_size)],
      ["Species", formatTableCell(ringing.species_name)],
      ["Sex", formatTableCell(ringing.sex)],
      ["Location", formatTableCell(`${ringing.location}, ${ringing.state_name}`)],
      // ["State", formatTableCell(ringing.state_name)],
      // ["Locality", formatTableCell(ringing.locality)],
      [
        "Ringing Date",
        formatDisplayDate(ringing.date, detailDateFormatOptions),
      ],
      ["Ringer", formatTableCell(ringing.ringer)],
    ]
    : [];

  const resightingSections = resightings.map((row, index) => {
    const decimalCoordinates = parseDecimalCoordinates(
      row.latitude,
      row.longitude
    );

    return {
      title:
        resightings.length > 1
          ? `Resighting details (Avifauna) #${index + 1}`
          : "Resighting details (Avifauna)",
      mapCoordinates: decimalCoordinates,
      rows: [
        ["Place Of Recovery", formatTableCell(row.location)],
        // ["State", formatTableCell(row.state_name)],
        ["Country", formatTableCell(row.country)],
        // ["Latitude", formatTableCell(row.latitude)],
        // ["Longitude", formatTableCell(row.longitude)],
        [
          "Coordinates (decimal)",
          decimalCoordinates
            ? `${formatDecimalCoordinate(decimalCoordinates.lat)}, ${formatDecimalCoordinate(
              decimalCoordinates.lon
            )}`
            : "",
        ],
        ["Date Of Recovery", formatDisplayDate(row.date, detailDateFormatOptions)],
        ["Remarks", formatTableCell(row.remarks)],
      ],
    };
  });

  return {
    hasData: Boolean(ringing || resightings.length > 0),
    ringingRows,
    resightingSections,
  };
};

const EMPTY_COMPARABLE_VALUES = new Set([
  "",
  "unspecified",
  "n/a",
  "-",
  "null",
]);

const RESIGHTING_COMPARISON_FIELDS = new Set([
  "Place Of Recovery",
  "Latitude",
  "Longitude",
  "Coordinates (decimal)",
  "Date Of Recovery",
  "Remarks",
]);

const RINGING_COMPARISON_FIELDS = new Set([
  "Ring number",
  "Species",
  "Sex",
  "Ringing Date",
  "Ringer",
]);

/** Avifauna ringing row label → digitized row label(s), tried in order. */
const RINGING_DIGITIZED_FIELD_LOOKUP = {
  "Ring number": ["Ring number"],
  "Ringing Date": ["Bird - Ringing Date"],
  "Ringer": ["Bird - Ringer"],
  "Sex": ["Sex"],
};

const rowsToLookup = (rows) => {
  const map = {};
  for (const row of rows || []) {
    if (!row?.length) {
      continue;
    }
    map[row[0]] = row[1];
  }
  return map;
};

const buildDigitizedRingingMap = (digitizedRecord) =>
  rowsToLookup(digitizedRecord?.ringingRows);

const buildDigitizedComparisonMap = (digitizedRecord) => ({
  ...buildDigitizedRingingMap(digitizedRecord),
  ...rowsToLookup(digitizedRecord?.resightingRows),
});

const lookupDigitizedValue = (digitizedMap, fieldLabels) => {
  for (const label of fieldLabels) {
    if (Object.prototype.hasOwnProperty.call(digitizedMap, label)) {
      return digitizedMap[label];
    }
  }
  return undefined;
};

const normalizeComparableText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  const normalized = String(value).trim().toLowerCase().replace(/\s+/g, " ");
  if (EMPTY_COMPARABLE_VALUES.has(normalized)) {
    return "";
  }
  return normalized;
};

const normalizeComparableDate = (value) => {
  const normalizedText = normalizeComparableText(value);
  if (!normalizedText) {
    return "";
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return normalizedText;
};

const normalizeRemarksText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  const lines = String(value)
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.trim());

  return lines.join("\n").trim().toLowerCase();
};

const decimalCoordinatesFromRowValues = (latitude, longitude) =>
  parseDecimalCoordinates(latitude, longitude);

const compareDecimalCoordinateObjects = (digitized, avifauna) => {
  if (!digitized && !avifauna) {
    return "match";
  }
  if (!digitized || !avifauna) {
    return "mismatch";
  }

  const tolerance = 0.0001;
  const latMatches = Math.abs(digitized.lat - avifauna.lat) < tolerance;
  const lonMatches = Math.abs(digitized.lon - avifauna.lon) < tolerance;
  return latMatches && lonMatches ? "match" : "mismatch";
};

const compareCoordinatePair = (digitizedLat, digitizedLon, avifaunaLat, avifaunaLon) =>
  compareDecimalCoordinateObjects(
    decimalCoordinatesFromRowValues(digitizedLat, digitizedLon),
    decimalCoordinatesFromRowValues(avifaunaLat, avifaunaLon)
  );

const parseDecimalCoordinateDisplay = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const parts = String(value)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) {
    return null;
  }

  const lat = Number.parseFloat(parts[0]);
  const lon = Number.parseFloat(parts[1]);
  if (Number.isNaN(lat) || Number.isNaN(lon)) {
    return null;
  }

  return { lat, lon };
};

const resolveAvifaunaDecimalCoordinates = (avifaunaMap, avifaunaValue, mapCoordinates) =>
  decimalCoordinatesFromRowValues(avifaunaMap.Latitude, avifaunaMap.Longitude) ||
  mapCoordinates ||
  parseDecimalCoordinateDisplay(avifaunaValue);

const getResightingFieldMatchStatus = (
  label,
  avifaunaValue,
  digitizedMap,
  avifaunaMap,
  { mapCoordinates = null } = {}
) => {
  if (!RESIGHTING_COMPARISON_FIELDS.has(label)) {
    return null;
  }

  const digitizedValue = digitizedMap[label];

  if (label === "Latitude" || label === "Longitude") {
    return compareCoordinatePair(
      digitizedMap.Latitude,
      digitizedMap.Longitude,
      avifaunaMap.Latitude ?? avifaunaValue,
      avifaunaMap.Longitude
    );
  }

  if (label === "Coordinates (decimal)") {
    return compareDecimalCoordinateObjects(
      decimalCoordinatesFromRowValues(digitizedMap.Latitude, digitizedMap.Longitude),
      resolveAvifaunaDecimalCoordinates(avifaunaMap, avifaunaValue, mapCoordinates)
    );
  }

  if (label === "Date Of Recovery") {
    const digitizedDate = normalizeComparableDate(digitizedValue);
    const avifaunaDate = normalizeComparableDate(avifaunaValue);
    if (!digitizedDate && !avifaunaDate) {
      return "match";
    }
    return digitizedDate === avifaunaDate ? "match" : "mismatch";
  }

  if (label === "Remarks") {
    const digitizedRemarks = normalizeRemarksText(digitizedValue);
    const avifaunaRemarks = normalizeRemarksText(avifaunaValue);
    if (!digitizedRemarks && !avifaunaRemarks) {
      return "match";
    }
    return digitizedRemarks === avifaunaRemarks ? "match" : "mismatch";
  }

  const digitizedText = normalizeComparableText(digitizedValue);
  const avifaunaText = normalizeComparableText(avifaunaValue);
  if (!digitizedText && !avifaunaText) {
    return "match";
  }
  return digitizedText === avifaunaText ? "match" : "mismatch";
};

const getDigitizedSpeciesCandidates = (digitizedRecord, digitizedMap) => {
  const meta = digitizedRecord?.ringingComparisonMeta;
  const candidates = [];

  if (meta) {
    const ringingSpecies = normalizeComparableText(meta.ringingSpecies);
    const speciesSynonym = normalizeComparableText(meta.speciesSynonym);
    if (ringingSpecies) {
      candidates.push(ringingSpecies);
    }
    if (speciesSynonym) {
      candidates.push(speciesSynonym);
    }
  }

  if (candidates.length > 0) {
    return candidates;
  }

  const displayValue = digitizedMap["Ringing Species"];
  if (!displayValue) {
    return [];
  }

  const displayText = String(displayValue);
  const synMatch = displayText.match(/\(syn:\s*([^)]+)\)/i);
  const mainName = synMatch ? displayText.slice(0, synMatch.index).trim() : displayText;
  const parsedMain = normalizeComparableText(mainName);
  const parsedSynonym = synMatch ? normalizeComparableText(synMatch[1]) : "";

  if (parsedMain) {
    candidates.push(parsedMain);
  }
  if (parsedSynonym) {
    candidates.push(parsedSynonym);
  }

  return candidates;
};

const compareRingingSex = (avifaunaValue, digitizedRingingMap) => {
  const digitizedValue = lookupDigitizedValue(
    digitizedRingingMap,
    RINGING_DIGITIZED_FIELD_LOOKUP.Sex
  );
  const avifaunaSex = normalizeComparableSex(avifaunaValue);
  const digitizedSex = normalizeComparableSex(digitizedValue);

  if (!avifaunaSex && !digitizedSex) {
    return "match";
  }
  return avifaunaSex === digitizedSex ? "match" : "mismatch";
};

const compareRingingSpecies = (avifaunaValue, digitizedRecord, digitizedMap) => {
  const avifaunaSpecies = normalizeComparableText(avifaunaValue);
  const digitizedCandidates = getDigitizedSpeciesCandidates(digitizedRecord, digitizedMap);

  if (!avifaunaSpecies && digitizedCandidates.length === 0) {
    return "match";
  }
  if (!avifaunaSpecies || digitizedCandidates.length === 0) {
    return "mismatch";
  }

  return digitizedCandidates.some((candidate) => candidate === avifaunaSpecies)
    ? "match"
    : "mismatch";
};

const compareNormalizedValues = (avifaunaValue, digitizedValue, normalizeValue) => {
  const avifaunaNormalized = normalizeValue(avifaunaValue);
  const digitizedNormalized = normalizeValue(digitizedValue);

  if (!avifaunaNormalized && !digitizedNormalized) {
    return "match";
  }
  return avifaunaNormalized === digitizedNormalized ? "match" : "mismatch";
};

const getRingingFieldMatchStatus = (
  label,
  avifaunaValue,
  digitizedRingingMap,
  digitizedRecord
) => {
  if (!RINGING_COMPARISON_FIELDS.has(label)) {
    return null;
  }

  if (label === "Species") {
    return compareRingingSpecies(avifaunaValue, digitizedRecord, digitizedRingingMap);
  }

  if (label === "Sex") {
    return compareRingingSex(avifaunaValue, digitizedRingingMap);
  }

  const digitizedFieldLabels = RINGING_DIGITIZED_FIELD_LOOKUP[label];
  if (!digitizedFieldLabels) {
    return null;
  }

  const digitizedValue = lookupDigitizedValue(digitizedRingingMap, digitizedFieldLabels);

  if (label === "Ringing Date") {
    return compareNormalizedValues(avifaunaValue, digitizedValue, normalizeComparableDate);
  }

  return compareNormalizedValues(avifaunaValue, digitizedValue, normalizeComparableText);
};

const annotateRingingRows = (ringingRows, digitizedRecord) => {
  const digitizedRingingMap = buildDigitizedRingingMap(digitizedRecord);
  return (ringingRows || []).map(([label, value]) => [
    label,
    value,
    getRingingFieldMatchStatus(label, value, digitizedRingingMap, digitizedRecord),
  ]);
};

const annotateResightingSections = (resightingSections, digitizedMap) =>
  (resightingSections || []).map((section) => {
    const avifaunaMap = rowsToLookup(section.rows);

    return {
      ...section,
      rows: section.rows.map(([label, value]) => [
        label,
        value,
        getResightingFieldMatchStatus(label, value, digitizedMap, avifaunaMap, {
          mapCoordinates: section.mapCoordinates,
        }),
      ]),
    };
  });

/** Adds per-row match status on Avifauna ringing/resighting vs digitized record rows. */
export const annotateAvifaunaResightingComparison = (avifaunaRecord, digitizedRecord) => {
  if (!avifaunaRecord?.hasData || !digitizedRecord) {
    return avifaunaRecord;
  }

  const hasRingingComparison =
    (digitizedRecord?.ringingRows?.length ?? 0) > 0 &&
    (avifaunaRecord.ringingRows?.length ?? 0) > 0;
  const hasResightingComparison =
    (digitizedRecord?.resightingRows?.length ?? 0) > 0 &&
    (avifaunaRecord.resightingSections?.length ?? 0) > 0;

  if (!hasRingingComparison && !hasResightingComparison) {
    return avifaunaRecord;
  }

  const digitizedMap = buildDigitizedComparisonMap(digitizedRecord);

  return {
    ...avifaunaRecord,
    comparisonEnabled: true,
    ringingRows: hasRingingComparison
      ? annotateRingingRows(avifaunaRecord.ringingRows, digitizedRecord)
      : avifaunaRecord.ringingRows,
    resightingSections: hasResightingComparison
      ? annotateResightingSections(avifaunaRecord.resightingSections, digitizedMap)
      : avifaunaRecord.resightingSections,
  };
};
